import React, {
  useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import * as S from './AuditsSelector.styled';
import EscapeButton from 'pages/components/EscapeButton/EscapeButton';
import { moveTmpPanel } from 'pages/Navigation/utils';
import Search from '@mui/icons-material/Search';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import Check from '@mui/icons-material/Check';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import Block from '@mui/icons-material/Block';
import Tune from '@mui/icons-material/Tune';
import FilterAltOff from '@mui/icons-material/FilterAltOff';
import MeetingRoomOutlined from '@mui/icons-material/MeetingRoomOutlined';
import PersonOutline from '@mui/icons-material/PersonOutline';
import PeopleOutline from '@mui/icons-material/PeopleOutline';
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined';
import Flight from '@mui/icons-material/Flight';
import TextFields from '@mui/icons-material/TextFields';
import LibraryAdd from '@mui/icons-material/LibraryAdd';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  DEFAULT_LOG_SEARCH_OPTIONS,
  LogSearchMatchMode,
  LogSearchOptions,
  selectLogSearch,
  selectLogSearchOptions,
  selectLogSearches,
  selectLogsChecked,
  selectLogsValueMode,
  selectAuditsCombineSku,
  selectIsAuditsReportLogsFocus,
  setLogSearch,
  setLogSearchOptions,
  setLogSearches,
  setAuditsCombineSku,
  setSingleLogTime,
} from '../redux';
import { useLogs, useLogsSelector } from '../hooks';
import { LogKey } from '../Logs';
import { sortAuditLabels } from '../logUtils';
import { hasActiveLogSearch, isDefaultLogSearchOptions } from '../logSearch';

const MAX_SEARCHES = 5;

type MultiOptionKey = keyof Pick<
  LogSearchOptions,
  | 'exactMatch'
  | 'negate'
  | 'hideArchives'
  | 'fromFullLogs'
>;

const MULTI_OPTIONS: {
  key: MultiOptionKey,
  title: string,
  hint: string,
  icon: React.ReactNode,
  /** Only for 2nd+ searches (union into previous results). */
  requiresPriorSearch?: boolean,
}[] = [
  {
    key: 'exactMatch',
    title: 'Exact match',
    hint: 'Require the full term, not a partial match',
    icon: <TextFields fontSize="inherit" />,
  },
  {
    key: 'negate',
    title: 'Exclude matches',
    hint: 'Show everything except what matches',
    icon: <Block fontSize="inherit" />,
  },
  {
    key: 'fromFullLogs',
    title: 'Add from prior results',
    hint: 'Search the results from before the last search and add matches',
    icon: <LibraryAdd fontSize="inherit" />,
    requiresPriorSearch: true,
  },
  {
    key: 'hideArchives',
    title: 'Hide archives',
    hint: 'Skip removed inventory items, groups, and deleted departments',
    icon: <DeleteOutline fontSize="inherit" />,
  },
];

type MatchOption = {
  id: Exclude<LogSearchMatchMode, 'any'>,
  title: string,
  hint: string,
  icon: React.ReactNode,
};

const MATCH_OPTIONS: MatchOption[] = [
  {
    id: 'room',
    title: 'Room name',
    hint: 'Match room tags only',
    icon: <MeetingRoomOutlined fontSize="inherit" />,
  },
  {
    id: 'staff',
    title: 'Staff username',
    hint: 'Match staff tags only',
    icon: <PersonOutline fontSize="inherit" />,
  },
  {
    id: 'customer',
    title: 'Guest',
    hint: 'Match guest tags only',
    icon: <PeopleOutline fontSize="inherit" />,
  },
  {
    id: 'department',
    title: 'Department',
    hint: 'Match department tags only',
    icon: <AccountTreeOutlined fontSize="inherit" />,
  },
  {
    id: 'ota',
    title: 'OTA',
    hint: 'Match OTA tags only',
    icon: <Flight fontSize="inherit" />,
  },
];

const chipModeIcon = (options: LogSearchOptions) => {
  if (options.matchMode === 'room') return <MeetingRoomOutlined fontSize="inherit" />;
  if (options.matchMode === 'staff') return <PersonOutline fontSize="inherit" />;
  if (options.matchMode === 'customer') return <PeopleOutline fontSize="inherit" />;
  if (options.matchMode === 'department') return <AccountTreeOutlined fontSize="inherit" />;
  if (options.matchMode === 'ota') return <Flight fontSize="inherit" />;
  if (options.fromFullLogs) return <LibraryAdd fontSize="inherit" />;
  if (options.negate) return <Block fontSize="inherit" />;
  return <Search fontSize="inherit" />;
};

const LogSearchOptionsMenu = ({
  options,
  onChange,
  compact,
  canClearAll,
  onClearAll,
  /** Show “Add from prior results” (2nd+ search chips / draft after first chip). */
  allowFromFullLogs,
}: {
  options: LogSearchOptions,
  onChange: (next: LogSearchOptions) => void,
  compact?: boolean,
  /** True when chips and/or draft text can be wiped. */
  canClearAll?: boolean,
  onClearAll?: () => void,
  allowFromFullLogs?: boolean,
}) => {
  const [open, setOpen] = useState(false);
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const custom = !isDefaultLogSearchOptions(options);
  const canClear = custom || Boolean(canClearAll);
  const filterOptions = MULTI_OPTIONS.filter(
    (opt) => !opt.requiresPriorSearch || allowFromFullLogs,
  );

  const updatePopupPos = () => {
    const anchor = anchorRef.current;
    const popup = popupRef.current;
    if (!anchor || !popup) return;
    const rect = anchor.getBoundingClientRect();
    const popupH = popup.offsetHeight;
    const popupW = popup.offsetWidth;
    const gap = 6;
    let top = rect.bottom + gap;
    if (top + popupH > window.innerHeight - 8) {
      top = Math.max(8, rect.top - popupH - gap);
    }
    let left = rect.left;
    left = Math.max(8, Math.min(left, window.innerWidth - popupW - 8));
    setPopupPos({ top, left });
  };

  useLayoutEffect(() => {
    if (!open) {
      setPopupPos(null);
      return undefined;
    }
    updatePopupPos();
    const onReposition = () => updatePopupPos();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, options, canClear, allowFromFullLogs]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (!anchorRef.current?.contains(e.target as Node)
        && !popupRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const toggleMulti = (key: MultiOptionKey) => {
    onChange({ ...options, [key]: !options[key] });
  };

  const toggleMatch = (id: MatchOption['id']) => {
    onChange({
      ...options,
      matchMode: options.matchMode === id ? 'any' : id,
    });
  };

  const clearAll = () => {
    if (!canClear) return;
    onChange({ ...DEFAULT_LOG_SEARCH_OPTIONS });
    onClearAll?.();
    setOpen(false);
  };

  return (
    <S.SearchOptionsAnchor ref={anchorRef}>
      <S.SearchOptionsBtn
        type="button"
        $open={open}
        $custom={custom}
        $compact={compact}
        aria-label="Search options"
        aria-expanded={open}
        title={custom ? 'Custom search options' : 'Search options'}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <Tune fontSize="inherit" />
      </S.SearchOptionsBtn>
      {open && (
        <S.SearchOptionsPopup
          ref={popupRef}
          role="menu"
          style={popupPos
            ? { top: popupPos.top, left: popupPos.left }
            : { visibility: 'hidden' }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <S.SearchOptionsSectionLabel>Filters</S.SearchOptionsSectionLabel>
          <S.SearchOptionsSection>
            {filterOptions.map((opt) => {
              const checkedOpt = Boolean(options[opt.key]);
              return (
                <S.SearchOptionRow
                  key={opt.key}
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={checkedOpt}
                  $checked={checkedOpt}
                  onClick={() => toggleMulti(opt.key)}
                >
                  <S.SearchOptionCheck $checked={checkedOpt}>
                    {checkedOpt ? <Check fontSize="inherit" /> : null}
                  </S.SearchOptionCheck>
                  <S.SearchOptionIcon>{opt.icon}</S.SearchOptionIcon>
                  <S.SearchOptionText>
                    <S.SearchOptionTitle>{opt.title}</S.SearchOptionTitle>
                    <S.SearchOptionHint>{opt.hint}</S.SearchOptionHint>
                  </S.SearchOptionText>
                </S.SearchOptionRow>
              );
            })}
          </S.SearchOptionsSection>
          <S.SearchOptionsDivider />
          <S.SearchOptionsSectionLabel>Match</S.SearchOptionsSectionLabel>
          <S.SearchOptionsSection>
            {MATCH_OPTIONS.map((opt) => {
              const checkedOpt = options.matchMode === opt.id;
              return (
                <S.SearchOptionRow
                  key={opt.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={checkedOpt}
                  $checked={checkedOpt}
                  onClick={() => toggleMatch(opt.id)}
                >
                  <S.SearchOptionCheck $checked={checkedOpt} $radio>
                    {checkedOpt ? <Check fontSize="inherit" /> : null}
                  </S.SearchOptionCheck>
                  <S.SearchOptionIcon>{opt.icon}</S.SearchOptionIcon>
                  <S.SearchOptionText>
                    <S.SearchOptionTitle>{opt.title}</S.SearchOptionTitle>
                    <S.SearchOptionHint>{opt.hint}</S.SearchOptionHint>
                  </S.SearchOptionText>
                </S.SearchOptionRow>
              );
            })}
          </S.SearchOptionsSection>
          <S.SearchOptionsDivider />
          <S.SearchOptionsClearBtn
            type="button"
            $disabled={!canClear}
            disabled={!canClear}
            aria-label="Clear all search"
            title="Clear all search"
            onClick={clearAll}
          >
            <FilterAltOff fontSize="inherit" />
            Clear all
          </S.SearchOptionsClearBtn>
        </S.SearchOptionsPopup>
      )}
    </S.SearchOptionsAnchor>
  );
};

export const AuditSelector = ({
  isWeb,
  useEscape,
  isNoSearch
}:{
  isWeb?: boolean,
  useEscape?: boolean,
  isNoSearch?: true
}) => {
  const dispatch = useAppDispatch();
  const checked = useAppSelector(selectLogsChecked);
  const logSearch = useAppSelector(selectLogSearch);
  const logSearchOptions = useAppSelector(selectLogSearchOptions);
  const logSearches = useAppSelector(selectLogSearches);
  const logsValueMode = useAppSelector(selectLogsValueMode);
  const auditsCombineSku = useAppSelector(selectAuditsCombineSku);
  const isUnitsMode = logsValueMode === 'units';
  const {
    logGroups,
    deletedDepartmentNames,
    deletedInventoryItemNames,
    deletedInventoryGroupNames,
  } = useLogs();
  const { onClickShowAll, onClickAudit, onClickAuditKey } = useLogsSelector({});
  const [expandedGroup, setExpandedGroup] = useState<LogKey | null>(null);
  const [groupTypeSearch, setGroupTypeSearch] = useState('');
  const [editingChip, setEditingChip] = useState<number | null>(null);
  const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(true);
  const isReportLogsFocus = useAppSelector(selectIsAuditsReportLogsFocus);

  const onAuditsScroll = (e: any, scrollTop?: number) => {
    const scrollT = scrollTop || e.target.scrollTop;
    localStorage.setItem('audits_scroll', (scrollT || 0).toString());
  };

  const scrollAuditsPos = () => {
    const scrollTop = Number(localStorage.getItem('audits_scroll') || 0);
    document.getElementById('audits')?.scrollTo({ top: scrollTop });
  };

  useLayoutEffect(() => {
    scrollAuditsPos();
  }, [checked]);

  const toggleExpand = (key: LogKey, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedGroup((prev) => {
      if (prev === key) {
        setGroupTypeSearch('');
        return null;
      }
      setGroupTypeSearch('');
      return key;
    });
  };

  const groupCheckState = (types: string[]): 'off' | 'partial' | 'on' => {
    if (!types.length) return 'off';
    const count = types.filter((t) => checked.includes(t)).length;
    if (count === 0) return 'off';
    if (count === types.length) return 'on';
    return 'partial';
  };

  const groupCheckedLabel = (types: string[]) => {
    if (!types.length) return '0';
    const count = types.filter((t) => checked.includes(t)).length;
    if (count === types.length) return 'all';
    if (count > 99) return '99+';
    return String(count);
  };

  const typesForGroup = (key: LogKey, types: string[]) => (
    key === 'Inventory' || key === 'Inventory Group'
      ? sortAuditLabels(types)
      : types
  );

  const draftTrimmed = logSearch.trim();
  const canAddSearch = Boolean(draftTrimmed) && logSearches.length < MAX_SEARCHES;
  const draftNegate = logSearchOptions.negate;
  const hasAnySearch = hasActiveLogSearch(logSearches, logSearch);

  const clearAllSearch = () => {
    dispatch(setLogSearches([]));
    dispatch(setLogSearch(''));
    dispatch(setLogSearchOptions({ ...DEFAULT_LOG_SEARCH_OPTIONS }));
  };

  const commitSearch = () => {
    if (!canAddSearch) return;
    dispatch(setLogSearches([
      ...logSearches,
      { term: draftTrimmed, options: { ...logSearchOptions } },
    ]));
    dispatch(setLogSearch(''));
    dispatch(setLogSearchOptions({ ...DEFAULT_LOG_SEARCH_OPTIONS }));
  };

  const removeSearch = (index: number) => {
    // Dropping a chip may promote a later “Add from prior results” chip to first — clear that flag.
    const next = logSearches
      .filter((_, i) => i !== index)
      .map((f, i) => (
        i === 0 && f.options.fromFullLogs
          ? { ...f, options: { ...f.options, fromFullLogs: false } }
          : f
      ));
    dispatch(setLogSearches(next));
    // Draft option only applies with a prior chip.
    if (next.length === 0 && logSearchOptions.fromFullLogs) {
      dispatch(setLogSearchOptions({ ...logSearchOptions, fromFullLogs: false }));
    }
  };

  const updateChipOptions = (index: number, options: LogSearchOptions) => {
    // First chip cannot union from prior results (nothing prior to add into).
    const nextOptions = index === 0 && options.fromFullLogs
      ? { ...options, fromFullLogs: false }
      : options;
    dispatch(setLogSearches(logSearches.map((f, i) => (
      i === index ? { ...f, options: nextOptions } : f
    ))));
  };

  const updateChipTerm = (index: number, term: string) => {
    dispatch(setLogSearches(logSearches.map((f, i) => (
      i === index ? { ...f, term } : f
    ))));
  };

  const commitChipTerm = (index: number, raw: string) => {
    const term = raw.trim();
    if (!term) {
      removeSearch(index);
      return;
    }
    if (term !== logSearches[index]?.term) {
      updateChipTerm(index, term);
    }
  };

  const draftIcon = () => {
    if (logSearchOptions.matchMode === 'room') return <MeetingRoomOutlined fontSize="medium" />;
    if (logSearchOptions.matchMode === 'staff') return <PersonOutline fontSize="medium" />;
    if (logSearchOptions.matchMode === 'customer') return <PeopleOutline fontSize="medium" />;
    if (logSearchOptions.matchMode === 'department') return <AccountTreeOutlined fontSize="medium" />;
    if (logSearchOptions.matchMode === 'ota') return <Flight fontSize="medium" />;
    if (logSearchOptions.fromFullLogs) return <LibraryAdd fontSize="medium" />;
    if (draftNegate) return <Block fontSize="medium" />;
    return <Search fontSize="medium" />;
  };

  const draftPlaceholder = () => {
    if (logSearches.length) {
      if (logSearchOptions.fromFullLogs) {
        return draftNegate ? 'Exclude from prior results…' : 'Add from prior results…';
      }
      return draftNegate ? 'Exclude from results…' : 'Refine search…';
    }
    if (logSearchOptions.matchMode === 'room') {
      return draftNegate ? 'Exclude room name…' : 'Search by room name';
    }
    if (logSearchOptions.matchMode === 'staff') {
      return draftNegate ? 'Exclude staff…' : 'Search by staff username';
    }
    if (logSearchOptions.matchMode === 'customer') {
      return draftNegate ? 'Exclude guest…' : 'Search by guest';
    }
    if (logSearchOptions.matchMode === 'department') {
      return draftNegate ? 'Exclude department…' : 'Search by department';
    }
    if (logSearchOptions.matchMode === 'ota') {
      return draftNegate ? 'Exclude OTA…' : 'Search by OTA';
    }
    return draftNegate ? 'Exclude from audit logs…' : 'Search audit logs';
  };

  // Web left panel stays mounted so width can ease open/closed.
  // Report Show logs: fully tuck the pane until Exit (no collapsed rail).
  const isPaneOpen = !isWeb || (isLeftPanelOpen && !isReportLogsFocus);

  return (
    <S.AuditsCont
      isWeb={isWeb}
      $open={isPaneOpen}
      $fullyHidden={Boolean(isWeb && isReportLogsFocus)}
      // Skip page-enter transforms so fixed/hide chrome does not get a containing-block bounce.
      {...(isWeb ? { 'data-page-enter-static': true } : {})}
    >
      {isWeb && !isReportLogsFocus && (
        <S.PanelToggleRow $collapsed={!isLeftPanelOpen}>
          <S.PanelTitle $hidden={!isLeftPanelOpen}>Audits</S.PanelTitle>
          <S.PanelToggle
            type="button"
            $expanded={isLeftPanelOpen}
            aria-expanded={isLeftPanelOpen}
            aria-label={isLeftPanelOpen ? 'Collapse audits panel' : 'Open audits panel'}
            title={isLeftPanelOpen ? 'Collapse audits panel' : 'Open audits panel'}
            onClick={() => setIsLeftPanelOpen((open) => !open)}
          >
            <ChevronLeft fontSize="small" />
          </S.PanelToggle>
        </S.PanelToggleRow>
      )}
      <S.PaneBody
        aria-hidden={isPaneOpen === false}
        $open={isPaneOpen}
      >
        <S.SearchCont $clearEscape={!isWeb && !isNoSearch} $belowPanelTitle={Boolean(isWeb)}>
        {
          !isNoSearch && (
            <>
              {logSearches.length > 0 && (
                <S.SearchChips>
                  {logSearches.map((filter, i) => {
                    const isEditing = editingChip === i;
                    const solidNegate = Boolean(filter.options.negate && isEditing);
                    return (
                      <S.SearchChip
                        key={`audit_search_${i}`}
                        $negate={filter.options.negate}
                        $active={isEditing}
                      >
                        <S.SearchChipIcon>
                          {chipModeIcon(filter.options)}
                        </S.SearchChipIcon>
                        <S.SearchChipInput
                          value={filter.term}
                          title={filter.term}
                          aria-label={`Edit saved search ${i + 1}`}
                          placeholder="Search term…"
                          onFocus={() => setEditingChip(i)}
                          onChange={(e) => updateChipTerm(i, e.target.value)}
                          onBlur={(e) => {
                            setEditingChip(null);
                            commitChipTerm(i, e.target.value);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              (e.target as HTMLInputElement).blur();
                            }
                          }}
                        />
                        <LogSearchOptionsMenu
                          compact
                          options={filter.options}
                          onChange={(next) => updateChipOptions(i, next)}
                          canClearAll={hasAnySearch}
                          onClearAll={clearAllSearch}
                          allowFromFullLogs={i > 0}
                        />
                        <S.SearchChipRemove
                          type="button"
                          $solid={solidNegate}
                          aria-label={`Remove search “${filter.term}”`}
                          onClick={() => removeSearch(i)}
                        >
                          <Close fontSize="inherit" />
                        </S.SearchChipRemove>
                      </S.SearchChip>
                    );
                  })}
                </S.SearchChips>
              )}
              <S.SearchRow>
                <S.SearchInputWrap>
                  {!logSearch ? (
                    <S.IconDiv isActive={false}>
                      {draftIcon()}
                    </S.IconDiv>
                  ) : null}
                  <S.Input
                    isActive={!!draftTrimmed}
                    $hasClear={Boolean(logSearch)}
                    $hideLeftIcon={Boolean(logSearch)}
                    placeholder={draftPlaceholder()}
                    value={logSearch}
                    onChange={(e) => {
                      dispatch(setLogSearch(e.target.value));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && canAddSearch) {
                        e.preventDefault();
                        commitSearch();
                      }
                    }}
                  />
                  {logSearch ? (
                    <S.ClearSearchBtn
                      type="button"
                      $isActive={!!draftTrimmed}
                      aria-label="Clear search"
                      title="Clear search"
                      onClick={() => dispatch(setLogSearch(''))}
                    >
                      <Close fontSize="inherit" />
                    </S.ClearSearchBtn>
                  ) : null}
                </S.SearchInputWrap>
                <LogSearchOptionsMenu
                  options={logSearchOptions}
                  onChange={(next) => {
                    // Draft “Add from prior results” only after at least one committed chip.
                    const safe = logSearches.length === 0 && next.fromFullLogs
                      ? { ...next, fromFullLogs: false }
                      : next;
                    dispatch(setLogSearchOptions(safe));
                  }}
                  canClearAll={hasAnySearch}
                  onClearAll={clearAllSearch}
                  allowFromFullLogs={logSearches.length > 0}
                />
                {canAddSearch && (
                  <S.AddSearchBtn
                    type="button"
                    aria-label="Add search filter"
                    title="Add search filter"
                    onClick={commitSearch}
                  >
                    <Add fontSize="inherit" />
                  </S.AddSearchBtn>
                )}
              </S.SearchRow>
            </>
          )
        }
        </S.SearchCont>
        {!isUnitsMode && !isNoSearch ? (
          <S.CombineSkuToggle
            type="button"
            title="Roll up rows with cash values by log type (SKU). Empty or N/A values stay separate."
            onClick={() => {
              const nextOn = !auditsCombineSku;
              dispatch(setAuditsCombineSku(nextOn));
              // Combine SKU cannot use per-log start/end — drop time range if it is on.
              if (nextOn) dispatch(setSingleLogTime(null));
            }}
          >
            <S.CombineSkuCheck
              type="checkbox"
              checked={auditsCombineSku}
              readOnly
              tabIndex={-1}
            />
            Combine SKU
          </S.CombineSkuToggle>
        ) : null}
        {/* Slide-in / tmp panel still uses the older right-aligned header */}
        {!isWeb && (
          <S.Header>
            Audits
          </S.Header>
        )}
        {
          useEscape ? <EscapeButton onClick={() => moveTmpPanel()} /> : (
            <S.EsacpePart>
              <EscapeButton onClick={() => moveTmpPanel()} />
            </S.EsacpePart>
          )
        }
        <S.Audits id="audits" onScroll={onAuditsScroll}>
        <S.AuditsTopSpacer />
        <S.AuditCont>
          <S.AuditGroup1
            $selected={checked.includes('Show all')}
            onClick={() => onClickShowAll(checked)}
          >
            <S.Select
              type="checkbox"
              checked={checked.includes('Show all')}
              readOnly
            />
            <S.AuditGroupLabel>Show all</S.AuditGroupLabel>
          </S.AuditGroup1>
        </S.AuditCont>
        {
          Object.entries(logGroups).map(([key, value]) => {
            const groupKey = key as LogKey;
            const isExpanded = expandedGroup === groupKey;
            const groupTypes = typesForGroup(groupKey, value);
            const searchTerm = groupTypeSearch.trim().toLowerCase();
            const visibleTypes = isExpanded && searchTerm
              ? groupTypes.filter((t) => t.toLowerCase().includes(searchTerm))
              : groupTypes;
            const checkState = groupCheckState(groupTypes);
            const groupHeader = (
              <S.AuditGroup
                $selected={checkState === 'on'}
                onClick={() => onClickAuditKey(groupKey, checked)}
              >
                <S.CheckBox $state={checkState} aria-hidden>
                  {checkState === 'on' ? <Check fontSize="inherit" /> : null}
                </S.CheckBox>
                <S.AuditGroupLabel>{key}</S.AuditGroupLabel>
                <S.DropDownMarker
                  onClick={(e) => toggleExpand(groupKey, e)}
                  title={isExpanded ? 'Collapse' : 'Expand'}
                >
                  <S.DropDownCount>{groupCheckedLabel(groupTypes)}</S.DropDownCount>
                  {isExpanded
                    ? <ExpandLess fontSize="small" />
                    : <ExpandMore fontSize="small" />}
                </S.DropDownMarker>
              </S.AuditGroup>
            );
            return (
              <S.AuditCont key={`AuditSet_${key}`} isCollapsed={!isExpanded}>
                {isExpanded ? (
                  <S.AuditGroupSticky>
                    {groupHeader}
                    <S.GroupTypeSearch onClick={(e) => e.stopPropagation()}>
                      <Search fontSize="inherit" />
                      <S.GroupTypeSearchInput
                        value={groupTypeSearch}
                        placeholder={`Search ${key}`}
                        aria-label={`Search ${key}`}
                        onChange={(e) => setGroupTypeSearch(e.target.value)}
                      />
                    </S.GroupTypeSearch>
                  </S.AuditGroupSticky>
                ) : groupHeader}
                {isExpanded && visibleTypes.map((logType) => {
                  const isDeletedDept = key === 'Departments'
                    && deletedDepartmentNames.has(logType);
                  const isDeletedInventoryItem = key === 'Inventory'
                    && deletedInventoryItemNames.has(logType);
                  const isDeletedInventoryGroup = key === 'Inventory Group'
                    && deletedInventoryGroupNames.has(logType);
                  const isDeleted = isDeletedDept
                    || isDeletedInventoryItem
                    || isDeletedInventoryGroup;
                  const deletedTitle = isDeletedDept
                    ? 'Deleted department'
                    : isDeletedInventoryItem
                      ? 'Removed or renamed inventory item'
                      : isDeletedInventoryGroup
                        ? 'Removed or renamed inventory group'
                        : undefined;
                  const isTypeSelected = checked.includes(logType);
                  return (
                    <S.AuditName
                      key={`logT_${logType}`}
                      isDeleted={isDeleted}
                      isSelected={isTypeSelected}
                      onClick={() => onClickAudit(groupKey, logType, checked)}
                    >
                      <S.Select2
                        type="checkbox"
                        checked={isTypeSelected}
                        readOnly
                      />
                      {isDeleted && (
                        <S.DeletedIcon title={deletedTitle}>
                          <DeleteOutline sx={{ fontSize: 14 }} />
                        </S.DeletedIcon>
                      )}
                      <S.AuditNameText isDeleted={isDeleted}>{logType}</S.AuditNameText>
                    </S.AuditName>
                  );
                })}
              </S.AuditCont>
            );
          })
        }
        </S.Audits>
      </S.PaneBody>
    </S.AuditsCont>
  );
};
